import Layout from "src/components/layout/Layout";
import { useNavigate, useParams } from "react-router-dom";
import useAuth from "src/hooks/selectors/useAuth";
import NotFound from "src/views/auth/NotFoundView";
import PublishInviteTask from "src/components/forms/TaskForm/elements/PublishInviteTask";
import StatisticsPane from "src/components/tasks/panes/StatisticsPane";
import SelectRegistrationPane from "src/components/tasks/panes/SelectRegistrationPane";
import useTask from "src/hooks/api/tasks/useTask";
import ApiCall from "src/components/ApiCall";
import RegistrationMatch from "src/components/tasks/panes/registration/student/RegistrationMatch";
import Chat from "src/components/chat/Chat";
import Card from "src/components/Card";
import ReviewForm from "src/components/forms/ReviewForm";
import DownloadAppBox from "src/components/promotion/share/DownloadAppBox";
import { useTranslation } from "react-i18next";
import TaskInvites from "src/components/tasks/TaskInvites";
import EditTaskView from "src/views/authenticated/tasks/create/EditTaskView";
import SubtasksGrid from "src/components/tasks/SubtasksGrid";
import TaskDetailWrapper from "src/components/tasks/detail/TaskDetailWrapper";
import TaskDetailEditor from "src/components/tasks/detail/TaskDetailEditor";
import FileList from "src/components/field/utils/FileList";
import useTaskForm from "src/hooks/api/tasks/useTaskForm";

const TasksDetailNormalView = () => {
	const { t } = useTranslation("task-view", {
		keyPrefix: "detail-view",
	});
	const { task: data, status } = useTask();
	const { actions } = useTaskForm();
	const auth = useAuth();
	const { pane, registration, extra } = useParams() as any;
	const navigate = useNavigate();

	if (pane === "edit" && auth.type === "company") {
		return <EditTaskView />;
	}

	const renderEmpty = () => (
		<div className="flex flex-col justify-center items-center flex-1">
			{!["idle", "error"].includes(status) ? (
				<span className="base-loader"></span>
			) : (
				<strong>{t("empty.title")}</strong>
			)}
		</div>
	);

	if (auth.type === "student") {
		if (!registration) {
			return <NotFound />;
		}

		return (
			<ApiCall query={`/student/registrations/${registration}`}>
				{(registration, loading, _, refetch) => {
					if (!registration?.id && !loading) {
						return (
							<Layout
								title={data?.title}
								loading={status === "loading" || loading}
							>
								{renderEmpty()}
							</Layout>
						);
					}
					return (
						<Layout
							title={data?.title}
							loading={status === "loading" || loading}
						>
							{extra === "chat" ? (
								<>
									{registration?.id && (
										<Chat
											options={{
												type: "task",
												room: `room-${registration.id}`,
												backUrl: `/tasks/${data?.hid}/show/${registration.hid}`,
											}}
											data={{
												task: data,
												registration,
											}}
											loading={false}
										/>
									)}
								</>
							) : extra === "accept" ? (
								<div className="flex flex-col gap-8">
									{registration?.is_accepted ? (
										<ReviewForm
											taskId={data.id}
											userId={registration?.user?.id}
											companyId={data?.company?.id}
											onDone={() => {
												if (refetch) {
													refetch();
												}
												navigate(
													`/tasks/${data?.hid}/show/${registration?.hid}`
												);
											}}
											variant={
												extra === "cancel"
													? "cancel"
													: undefined
											}
											onCancel={() => {
												if (refetch) {
													refetch();
												}
												navigate(
													`/tasks/${data?.hid}/show/${registration?.hid}`
												);
											}}
										/>
									) : (
										<Card title={t("not-selected.title")}>
											<p>
												{t("not-selected.description")}
											</p>
										</Card>
									)}
								</div>
							) : (
								<RegistrationMatch {...{ registration }} />
							)}
							<div className="flex justify-center items-center">
								<DownloadAppBox size="medium" />
							</div>
						</Layout>
					);
				}}
			</ApiCall>
		);
	}

	return (
		<TaskDetailWrapper task={data} loading={status === "loading"}>
			{data?.id ? (
				<>
					{pane === "invites" && (
						<>
							<TaskInvites task={data} />
							{!data.is_visible && !data.is_matched && (
								<PublishInviteTask
									task={data}
									onSubmit={(task) =>
										window.location.reload()
									}
								/>
							)}
						</>
					)}
					{pane === "statistics" && data?.id && (
						<StatisticsPane task={data} />
					)}

					{pane === "editor" && data?.id && <TaskDetailEditor />}

					{pane === "show" &&
						!data?.has_subtasks &&
						data.type === "PUBLISHED" && <SelectRegistrationPane />}

					{pane === "show" && data.has_subtasks && (
						<SubtasksGrid task={data} />
					)}
					{pane === "files" && (
						<div className="container">
							<FileList
								files={data?.media || []}
								onChange={(media) =>
									actions.set({
										media,
									})
								}
							/>
						</div>
					)}
				</>
			) : (
				<div className="flex flex-col justify-center items-center flex-1">
					{!["idle", "error"].includes(status) ? (
						<span className="base-loader"></span>
					) : (
						<strong>{t("empty.title")}</strong>
					)}
				</div>
			)}
		</TaskDetailWrapper>
	);
};

export default TasksDetailNormalView;
